import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

const EMPTY = {
  tier: 'free',
  isPremium: false,
  isTrial: false,
  isSuperadmin: false,
  role: 'free',
  companyId: null,
  companyName: null,
  companyLogo: null,
  accessibleFeatures: [],
  extraModules: [],
  exportPermissions: { crm: false, accounting: false },
  trialRestrictions: null,
  trialDaysRemaining: null,
  firstName: null,
  expiresAt: null,
  daysRemaining: null,
  loading: false,
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState({ ...EMPTY, loading: true });

  const fetchSubscription = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setSubscription({ ...EMPTY, loading: false });
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };
    const base = process.env.REACT_APP_BACKEND_URL;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      // Fetch legacy subscription info (trial badges, expiry) AND the new
      // tier-based access info (effective tier + accessible module list).
      const [subRes, accessRes] = await Promise.all([
        fetch(`${base}/api/users/me/subscription`, { headers, signal: controller.signal }),
        fetch(`${base}/api/users/me/access`, { headers, signal: controller.signal }),
      ]);
      clearTimeout(timeoutId);

      const sub = subRes.ok ? await subRes.json() : {};
      const access = accessRes.ok ? await accessRes.json() : {};

      setSubscription({
        // Effective tier/access wins over legacy individual subscription.
        tier: access.tier ?? sub.subscription_tier ?? 'free',
        isPremium: access.is_premium ?? sub.is_premium ?? false,
        isSuperadmin: access.is_superadmin ?? false,
        role: access.role ?? 'free',
        companyId: access.company_id ?? null,
        companyName: access.company_name ?? null,
        companyLogo: access.company_logo || null,
        accessibleFeatures: access.accessible_features ?? [],
        extraModules: access.extra_modules ?? [],
        exportPermissions: access.export_permissions ?? { crm: false, accounting: false },
        isTrial: sub.is_trial ?? false,
        trialRestrictions: sub.trial_restrictions ?? null,
        trialDaysRemaining: sub.trial_days_remaining ?? null,
        firstName: sub.first_name ?? null,
        expiresAt: sub.subscription_expires_at ?? null,
        daysRemaining: sub.days_remaining ?? null,
        loading: false,
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Subscription fetch timeout');
      } else {
        console.error('Error fetching subscription:', error);
      }
      setSubscription({ ...EMPTY, loading: false });
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const refreshSubscription = useCallback(async () => {
    await fetchSubscription();
  }, [fetchSubscription]);

  const clearSubscription = useCallback(() => {
    setSubscription({ ...EMPTY, loading: false });
  }, []);

  // Single source of truth for per-module gating.
  const canAccess = useCallback(
    (featureId) => {
      if (subscription.isSuperadmin) return true;
      if (!featureId) return subscription.isPremium;
      return subscription.accessibleFeatures.includes(featureId);
    },
    [subscription.isSuperadmin, subscription.isPremium, subscription.accessibleFeatures],
  );

  // Per-area data-export gating (CRM / Buchhaltung). Admins always true.
  const canExport = useCallback(
    (area) => {
      if (subscription.isSuperadmin) return true;
      return !!(subscription.exportPermissions || {})[area];
    },
    [subscription.isSuperadmin, subscription.exportPermissions],
  );

  // Löschen von Stammdaten (Artikel/Kontakte) ist nur Firmen-Admins,
  // Super-Admins und Einzelnutzern erlaubt – nicht angestellten Mitarbeitern
  // (company_user) oder Steuerberatern.
  const canDelete =
    subscription.isSuperadmin ||
    !['company_user', 'tax_advisor'].includes(subscription.role);

  return (
    <SubscriptionContext.Provider value={{ ...subscription, refreshSubscription, clearSubscription, canAccess, canExport, canDelete }}>
      {children}
    </SubscriptionContext.Provider>
  );
};
